import { useCallback, useEffect, useState } from 'react';
import { expectArray } from '../../../apiShape.js';
import { toUserFacingMessage } from '../../../userFacingError.js';
export function useClickUpExportState(runtime) {
    const [workspaces, setWorkspaces] = useState([]);
    const [workspaceId, setWorkspaceId] = useState('');
    const [spaces, setSpaces] = useState([]);
    const [spaceId, setSpaceId] = useState('');
    const [lists, setLists] = useState([]);
    const [listId, setListId] = useState('');
    const [spacesLoading, setSpacesLoading] = useState(false);
    const [listsLoading, setListsLoading] = useState(false);
    const [spacesError, setSpacesError] = useState(null);
    const [listsError, setListsError] = useState(null);
    const [spacesRetry, setSpacesRetry] = useState(0);
    const [listsRetry, setListsRetry] = useState(0);
    // Clear children in the gesture itself, before the next effect can run, so
    // Export never sees a list from the previous workspace/space during loading.
    const chooseSpace = useCallback((value) => {
        setSpaceId(value);
        setListId('');
        setLists([]);
        setListsError(null);
    }, []);
    const chooseWorkspace = useCallback((value) => {
        setWorkspaceId(value);
        setSpaces([]);
        setSpacesError(null);
        chooseSpace('');
    }, [chooseSpace]);
    const onDataLoaded = useCallback((items) => {
        setWorkspaces(items);
        chooseWorkspace(items[0]?.id ?? '');
    }, [chooseWorkspace]);
    useEffect(() => {
        if (!workspaceId) {
            setSpacesLoading(false);
            return;
        }
        let cancelled = false;
        setSpacesLoading(true);
        setSpacesError(null);
        runtime.api.getClickUpSpaces(workspaceId).then(response => {
            if (cancelled)
                return;
            const items = expectArray(response, 'ClickUp spaces');
            setSpaces(items);
            chooseSpace(items[0]?.id ?? '');
        }).catch(error => {
            if (cancelled)
                return;
            setSpaces([]);
            chooseSpace('');
            setSpacesError(toUserFacingMessage(error, "Couldn't load ClickUp spaces. Try again."));
        }).finally(() => { if (!cancelled)
            setSpacesLoading(false); });
        return () => { cancelled = true; };
    }, [workspaceId, spacesRetry, runtime, chooseSpace]);
    useEffect(() => {
        if (!spaceId) {
            setListsLoading(false);
            return;
        }
        let cancelled = false;
        setListsLoading(true);
        setListsError(null);
        runtime.api.getClickUpLists(spaceId).then(response => {
            if (cancelled)
                return;
            const items = expectArray(response, 'ClickUp lists');
            setLists(items);
            setListId(items[0]?.id ?? '');
        }).catch(error => {
            if (cancelled)
                return;
            setLists([]);
            setListId('');
            setListsError(toUserFacingMessage(error, "Couldn't load ClickUp lists. Try again."));
        }).finally(() => { if (!cancelled)
            setListsLoading(false); });
        return () => { cancelled = true; };
    }, [spaceId, listsRetry, runtime]);
    const reset = useCallback(() => { setWorkspaces([]); chooseWorkspace(''); }, [chooseWorkspace]);
    return { workspaces, workspaceId, spaces, spaceId, lists, listId, setListId, chooseWorkspace, chooseSpace,
        spacesLoading, listsLoading, spacesError, listsError, onDataLoaded, reset,
        retrySpaces: () => setSpacesRetry(n => n + 1), retryLists: () => setListsRetry(n => n + 1) };
}
//# sourceMappingURL=useClickUpExportState.js.map